import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Layout } from './components/Layout';
import { BankAccountsPage } from './pages/BankAccountsPage';
import { AddBankAccountPage } from './pages/AddBankAccountPage';
import { AccountDetailsPage } from './pages/AccountDetailsPage';
import { AllTransactionsPage } from './pages/AllTransactionsPage';
import { BillingPage } from './pages/BillingPage';
import { PlansPage } from './pages/PlansPage';
import { CreditsPage } from './pages/CreditsPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { CheckoutCallbackPage } from './pages/CheckoutCallbackPage';
import { ConsentCallbackPage } from './pages/ConsentCallbackPage';
import { LoginPage } from './pages/LoginPage';
import { NoPermissionPage } from './pages/NoPermissionPage';
import { NotificationSettingsPage } from './pages/NotificationSettingsPage';
import { useAuthStore } from './stores/authStore';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

/**
 * Auth guard component — redirects to /login if the user is not authenticated.
 * Shows a loading spinner while the auth state is being checked.
 */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Remember where the user was headed so login returns them there (not to "/").
    const from = location.pathname + location.search;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  return <>{children}</>;
}

function App() {
  const { checkAuth } = useAuthStore();

  // Read the BFF auth status on app startup (also primes the CSRF cookie).
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <BrowserRouter>
      <SentryRoutes>
        {/* Auth routes — outside Layout, no auth guard */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/no-permission" element={<NoPermissionPage />} />

        {/* Protected routes — wrapped in auth guard and Layout */}
        <Route
          path="/"
          element={
            <AuthGuard>
              <Layout />
            </AuthGuard>
          }
        >
          <Route index element={<BankAccountsPage />} />
          <Route path="accounts/add" element={<AddBankAccountPage />} />
          <Route path="accounts/:id" element={<AccountDetailsPage />} />
          <Route path="transactions" element={<AllTransactionsPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="billing/plans" element={<PlansPage />} />
          <Route path="billing/credits" element={<CreditsPage />} />
          <Route path="billing/checkout" element={<CheckoutPage />} />
          <Route path="billing/checkout/callback" element={<CheckoutCallbackPage />} />
          {/* Product-update emails link here (the only place to turn them off). */}
          <Route path="settings/notifications" element={<NotificationSettingsPage />} />
        </Route>

        {/* Consent callback routes - outside Layout for full-page experience */}
        <Route path="/consent/callback" element={<ConsentCallbackPage />} />
        <Route path="/consent/success" element={<ConsentCallbackPage />} />
        <Route path="/consent/pending" element={<ConsentCallbackPage />} />
        <Route path="/consent/failed" element={<ConsentCallbackPage />} />
        {/* SIBS integration service callback route */}
        <Route path="/sibs/consent/callback" element={<ConsentCallbackPage />} />
      </SentryRoutes>
    </BrowserRouter>
  );
}

export default App;
